import { NavController, Platform } from '@ionic/angular';
import {
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  ChangeDetectionStrategy,
  NgZone,
} from '@angular/core';
import { ServicesStartupService } from './services/services-startup/services-startup.service';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';

import { EventService } from './services/events/event.service';
import { environment } from 'src/environments/environment';
import { mainMenuComponent, homeDefaultTab } from 'src/environments/environment-layout';
import { sharedConfig } from 'src/environments/shared-config';
import { SecureStorageKey } from './services/storage/models/secure-storage-key.enum';
import { SecureStorageService } from './services/storage/secure-storage.service';
import { LoadingService } from './services/loading-service/loading.service';
import { TranslateService } from '@ngx-translate/core';
import { TranslatorService } from './services/translator-service/translator.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { OrganizationTokenModel } from './api/proxy/organization/organization-models';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { EnvironmentConfigService, ILocalAppEnvironment } from './services/environment-config-service/environment-config.service';
import { Dialog } from '@capacitor/dialog';
import { Network } from '@capacitor/network';
import { UserInfoService } from './services/auth-service/user-info-service';
import { SafeAreaService } from './services/safe-area-service/safe-area-service';
import { PageTitleService } from './services/page-title/page-title.service';
import { flavourChatRoutes, flavourFullScreenRoutes } from './flavour-routes';

import { MediaService } from './services/media-service/media.service';
import { MediaChatbotPage, IInitialQuestion } from './pages/home/media-chatbot/media-chatbot.page';
import { Browser } from '@capacitor/browser';
import { LocaleService } from './services/locale-service/locale.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly mainMenuComponent = mainMenuComponent;
  readonly showUatBuildBanner = environment.showUatBuildBanner ?? false;
  // public showMenu = true;
  busyLoading = false;
  isReady = false;
  chatAllowed = false;
  chatRouteAllowed = false;
  appDisabled = false;
  updateAvailable = false;
  allowFullScreen = false;
  public environmentLoaded = false;
  private networkListener: PluginListenerHandle | null = null;
  /** Debounce timer for network status so we don't run on the same tick as ConnectivityManager/WebView (ANR mitigation). */
  private networkStatusDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private networkRecheckTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onWindowFocus = () => this.queueNetworkStatusRefresh();
  private appStateListener: PluginListenerHandle | null = null;
  private appUrlOpenListener: PluginListenerHandle | null = null;
  private browserFinishedListener: PluginListenerHandle | null = null;
  private aiBotMessageSubscription?: Subscription;
  private aiBotIntroMessageSubscription?: Subscription;
  private closeMiniChatRequestSubscription?: Subscription;
  private vivaBotHiddenSubscription?: Subscription;
  private openMiniChatRequestSubscription?: Subscription;
  /** When true, hide VivaBot (e.g. health score customise mode). */
  vivaBotHiddenByCustomiseMode = false;
  public isOnline: boolean = true;

  // mini bubble state
  miniChatText = '';
  showMoreInfoButton = true; // Control whether to show More Info button
  private miniChatQuestionText?: string;
  private miniChatSourceRoute?: string;

  private inAIRedirect = false;
  private lastDeepLinkUrl: string | null = null;
  private lastDeepLinkAt = 0;
  /** Cold-start launch URL stored until the router outlet is ready to navigate. */
  private pendingDeepLinkNavigation: string | null = null;
  private deepLinkNavigationApplied = false;

  constructor(
    public servicesStartupService: ServicesStartupService,
    public navCtrl: NavController,
    private renderer: Renderer2,
    private _translate: TranslateService,
    private _translator: TranslatorService,
    private platform: Platform,
    public router: Router,
    private _EnvironmentConfigService: EnvironmentConfigService,
    private _safeAreaService: SafeAreaService,
    private _media: MediaService,
    private _pageTitleService: PageTitleService,
    private _localeService: LocaleService,
    private zone: NgZone,
  ) {
    this.platform.ready().then((value: string) => {
      setTimeout(async () => {
        try {
          await this.initializeApp();
        } catch (err) {
          console.error('[APP CONSTRUCTOR] App initialization failed', err);
        } finally {
          // Only hide splash screen once to prevent Android SurfaceControl issues
          await this.hideSplashScreen();
        }
      }, 500);
    });
  }

  async initializeApp() {
    await this._safeAreaService.init()
    await App.removeAllListeners();

    // Clean up existing listeners before adding new ones
    if (this.appUrlOpenListener) {
      this.appUrlOpenListener.remove();
      this.appUrlOpenListener = null;
    }
    if (this.appStateListener) {
      this.appStateListener.remove();
      this.appStateListener = null;
    }
    
    this.appUrlOpenListener = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      // FIX: Force the browser to close immediately when a deep link is received
      Browser.close().catch(err => console.log('Browser close ignored', err));

      if (event.url?.startsWith('msauth')) {
        setTimeout(() => void this.processMsauthCallbackUrl(event.url), 500);
        return;
      }

      void this.processDeepLinkUrl(event.url);
    });

    try {
      const launch = await App.getLaunchUrl();
      if (launch?.url) {
        await this.processDeepLinkUrl(launch.url);
      }
    } catch (e) {
      console.error('Error reading launch URL:', e);
    }

    this.appStateListener = await App.addListener('appStateChange', async (state) => {
      if (state.isActive) {
        await this.appResumed();
      } else {
        await this.appPaused();
      }
    });

    // In-app browser (privacy policy, terms, etc.) can leave status bar / notch insets wrong.
    if (Capacitor.isNativePlatform()) {
      if (this.browserFinishedListener) {
        this.browserFinishedListener.remove();
        this.browserFinishedListener = null;
      }
      this.browserFinishedListener = await Browser.addListener('browserFinished', () => {
        this.reapplySafeAreaAfterExternalSurface();
      });
    }

    LoadingService.disableSilentLoading();

    LoadingService.init();
    await this._translate.setFallbackLang(this._localeService.getDefaultCulture());

    const existingUser = await UserInfoService.getUserInfo();
    await this._localeService.init(existingUser?.language);


    EventService.loadingEvent.subscribe(async (loading: number) => {
      if (LoadingService.loadingAnimationEnabled())
        this.busyLoading = loading > 0;
      else
        this.busyLoading = false;
    });

    EventService.environmentLoaded.subscribe(async (config: ILocalAppEnvironment) => {
      this.environmentLoaded = true;
      
      // Only set these flags if the environment actually indicates a problem
      // This prevents race conditions where temporary states hide the main content
      if (config && config.versionDisabled === true) {
        this.appDisabled = true;
      }
      if (config && config.updateRequired === true) {
        this.updateAvailable = true;
      }
    });

    // Check if environment is already loaded (race condition fix)
    // If the environment was loaded before we subscribed, we need to manually trigger the handler
    // Do this immediately after subscription setup, not in setTimeout
    const existingConfig = this._EnvironmentConfigService.getCurrentConfig();
    if (existingConfig && !this.environmentLoaded) {
      this.environmentLoaded = true;
      
      // Only set these flags if the environment actually indicates a problem
      if (existingConfig && existingConfig.versionDisabled === true) {
        this.appDisabled = true;
      }
      if (existingConfig && existingConfig.updateRequired === true) {
        this.updateAvailable = true;
      }
    }
    
    // Also check again after a short delay in case environment loads between subscription and check
    setTimeout(async () => {
      const existingConfigDelayed = this._EnvironmentConfigService.getCurrentConfig();
      if (existingConfigDelayed && !this.environmentLoaded) {
        this.environmentLoaded = true;
        
        // Only set these flags if the environment actually indicates a problem
        if (existingConfigDelayed && existingConfigDelayed.versionDisabled === true) {
          this.appDisabled = true;
        }
        if (existingConfigDelayed && existingConfigDelayed.updateRequired === true) {
          this.updateAvailable = true;
        }
      }
    }, 1000);

    EventService.organizationConfigLoaded.subscribe(async (config: OrganizationTokenModel) => {
      this.chatAllowed = !!config && config.aiChatEnabled === true;
      if (this.chatAllowed) await this.checkChatRoute();
    });


    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        try {
          this.allowFullScreen = this.isFullScreenRoute(url);
          this.chatRouteAllowed = this.isChatRouteAllowed(url);
          LoadingService.disableSilentLoading();
          
          // Reset AI chat state on route change UNLESS navigating to the media chatbot page
          // (need to preserve initialRequest for full chat to process)
          if (!url.toLowerCase().includes('/home/mediachatbot')) {
            MediaChatbotPage.initialRequest = undefined;
            this.miniChatText = '';
          }
          this.syncShowMoreInfoButtonForRoute(url);
        } catch {
          this.chatRouteAllowed = false;
        }
      });


    await this.refreshOnlineState();
    if (!this.isOnline) {
      this.networkRecheckTimer = setTimeout(() => {
        this.networkRecheckTimer = null;
        void this.refreshOnlineState();
      }, 2000);
    }

    if (!Capacitor.isNativePlatform()) {
      window.addEventListener('focus', this.onWindowFocus);
    }

    // Listen for changes. Defer + debounce to avoid ANR: the native callback runs on main thread
    // during ConnectivityManager/WebView onLinkPropertiesChanged; we must not add work on that tick.
    this.networkListener = await Network.addListener('networkStatusChange', () => {
      this.queueNetworkStatusRefresh();
    });

    await this.servicesStartupService.initServices();

    const initialUrl = this.router.url;
    this.allowFullScreen = this.isFullScreenRoute(initialUrl);
    this.chatRouteAllowed = this.isChatRouteAllowed(initialUrl);

    if (!this.isWebViewCompatible()) {
      this.isReady = false;
      setTimeout(async () => {
        await Dialog.alert({
          title: 'WebView Update Required',
          message: 'Your device is using an outdated WebView that may cause errors. Please update Chrome or Android System WebView in the Play Store.',
        });
        await App.exitApp();
      }, 1000);
    }


    // iOS may only expose the cold-start launch URL after native init; re-read before first navigation.
    try {
      const launch = await App.getLaunchUrl();
      if (launch?.url && !launch.url.startsWith('msauth')) {
        await this.processDeepLinkUrl(launch.url);
      }
    } catch (e) {
      console.error('Error reading launch URL after init:', e);
    }

    await this.flushPendingDeepLinkNavigation();

    this.isReady = true;
    this._pageTitleService.start();

    // Add a fallback timeout to ensure main content renders even if environment loads late
    setTimeout(() => {
      if (!this.environmentLoaded) {
        // Don't change appDisabled or updateAvailable, just ensure we don't get stuck
      }
    }, 3000); // 3 second timeout

    // Add router fallback to ensure default route loads
    setTimeout(() => {
      if (this.deepLinkNavigationApplied) {
        return;
      }
      if (this.router.url === '/' || this.router.url === '') {
        this.router.navigateByUrl(`/home/${homeDefaultTab}`);
      }
    }, 1000); // 1 second timeout for router

    EventService.aiBotAwakeState.emit(false); //switch off the bot and make it go to sleep to wait for app startups to complete

    // Listen for bot messages from components
    // Subscribe early to avoid missing messages, but use a flag to ensure proper initialization
    this.aiBotMessageSubscription = EventService.aiBotMessage.subscribe((message: string) => {
      if (message && message.length > 0) {
        this.miniChatText = message;
        // Hide More Info button for group score messages
        // Group messages follow patterns: "Your {category} score has improved...", 
        // "Your {category} score has decreased...", or "Your {category} score is..."
        // Group names can be multi-word (e.g., "Heart and Blood Pressure")
        // Use a more robust check that matches the actual message patterns
        const trimmedMessage = message.trim();
        // Match "Your" followed by one or more words (including spaces and "and"), then "score", then the action
        const isGroupScoreMessage = /^Your\s+[\w\s]+?\s+score\s+(has\s+(improved|decreased)|is)/i.test(trimmedMessage);
        this.syncShowMoreInfoButtonForRoute(undefined, !isGroupScoreMessage);
      }
    });

    this.aiBotIntroMessageSubscription = EventService.aiBotIntroMessage.subscribe((message: string) => {
      if (message?.length) {
        this.miniChatText = message;
        this.showMoreInfoButton = false;
      }
    });

    this.closeMiniChatRequestSubscription = EventService.closeMiniChatRequest.subscribe(() => {
      void this.closeMiniChat();
    });

    this.openMiniChatRequestSubscription = EventService.openMiniChatRequest.subscribe((req: IInitialQuestion) => {
      this.openMiniChat(req);
    });

    // Splash screen will be hidden in the finally block above

  }

  private splashScreenHidden = false;

  /**
   * Safely hide the splash screen with proper error handling
   * Prevents multiple calls that can cause Android SurfaceControl issues
   */
  private async hideSplashScreen(): Promise<void> {
    if (this.splashScreenHidden) {
      return; // Already hidden, prevent multiple calls
    }

    try {
      await SplashScreen.hide();
      this.splashScreenHidden = true;
    } catch (error) {
      // Silently handle any splash screen hiding errors
      // This prevents crashes on Android when SurfaceControl is already released
      console.warn('Splash screen hide failed (this is usually safe to ignore):', error);
      this.splashScreenHidden = true; // Mark as hidden to prevent retries
    }
  }

  isWebViewCompatible(): boolean {
    if (Capacitor.getPlatform() != "android")
      return true;

    if (Capacitor.getPlatform() !== 'android') return true;
    const match = navigator.userAgent.match(/Chrome\/(\d+)/);
    if (!match) return true;
    const version = parseInt(match[1], 10);
    return version >= 93;
  }

  async checkChatRoute() {
    const url = this.router.url;
    this.chatRouteAllowed = this.isChatRouteAllowed(url);
  }

  async appResumed() {
    try {
      // Browser / external page return often corrupts notch / status-bar safe area until kill.
      this.reapplySafeAreaAfterExternalSurface();

      await this.servicesStartupService.resumeApp();
      
      const userInfo = await UserInfoService.getUserInfo();
      const profileLanguage = await this._localeService.getCachedProfileLanguageCode();
      if (profileLanguage) {
        await this._translator.useLanguage(profileLanguage, true);
      } else if (userInfo && userInfo.language) {
        await this._translator.useLanguage(userInfo.language, true);
      }

      if (Capacitor.isNativePlatform()) {
        if (userInfo && userInfo.access_token) {
          setTimeout(async () => {
     
            EventService.appResumed.emit();
          }, 1); // delay to let other resume logic settle
        } else {
          EventService.appResumed.emit();
        }
      } else {
        EventService.appResumed.emit();
      }

      EventService.appResumed.emit();
    } catch (error) {
      EventService.appResumed.emit();
    }
  }

  /** Staggered reapply so WebView layout sticks after SFSafariViewController / Custom Tabs. */
  private reapplySafeAreaAfterExternalSurface(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    setTimeout(() => void this._safeAreaService.reapplyAfterNativeView(), 100);
    setTimeout(() => void this._safeAreaService.reapplyAfterNativeView({ skipFlicker: true }), 350);
    setTimeout(() => void this._safeAreaService.reapplyAfterNativeView({ skipFlicker: true }), 600);
  }

  async appPaused() {
    EventService.appPaused.emit();
  }

  async ngOnInit(): Promise<void> {
    setTimeout(() => {
      if (!Capacitor.isNativePlatform()) {
        this.renderer.addClass(document.body, 'web-platform');
      }
    }, 500);

    this.vivaBotHiddenSubscription = EventService.vivaBotHiddenByCustomiseMode.subscribe((hidden: boolean) => {
      this.vivaBotHiddenByCustomiseMode = hidden;
    });
  }

  private queueNetworkStatusRefresh(): void {
    if (this.networkStatusDebounceTimer != null) {
      clearTimeout(this.networkStatusDebounceTimer);
    }
    this.networkStatusDebounceTimer = setTimeout(() => {
      this.networkStatusDebounceTimer = null;
      void this.refreshOnlineState();
    }, 100);
  }

  /** Re-read plugin status (corrected when the browser flag is a false offline). */
  private async refreshOnlineState(): Promise<void> {
    let connected = true;
    try {
      const status = await Network.getStatus();
      connected = !!status.connected;
    } catch {
      connected = typeof navigator === 'undefined' || navigator.onLine !== false;
    }
    this.zone.run(() => {
      if (this.isOnline === connected) return;
      this.isOnline = connected;
      EventService.networkStatusChange.emit(connected);
    });
  }

  async ngOnDestroy(): Promise<void> {
    // Clean up network listener and debounce timer
    if (this.networkStatusDebounceTimer != null) {
      clearTimeout(this.networkStatusDebounceTimer);
      this.networkStatusDebounceTimer = null;
    }
    if (this.networkRecheckTimer != null) {
      clearTimeout(this.networkRecheckTimer);
      this.networkRecheckTimer = null;
    }
    if (!Capacitor.isNativePlatform()) {
      window.removeEventListener('focus', this.onWindowFocus);
    }
    if (this.networkListener) {
      this.networkListener.remove();
      this.networkListener = null;
    }
    
    // Clean up app state listener
    if (this.appStateListener) {
      this.appStateListener.remove();
      this.appStateListener = null;
    }
    
    // Clean up app URL open listener
    if (this.appUrlOpenListener) {
      this.appUrlOpenListener.remove();
      this.appUrlOpenListener = null;
    }

    if (this.browserFinishedListener) {
      this.browserFinishedListener.remove();
      this.browserFinishedListener = null;
    }
    
    // Clean up AI bot message subscription
    if (this.aiBotMessageSubscription) {
      this.aiBotMessageSubscription.unsubscribe();
      this.aiBotMessageSubscription = undefined;
    }
    if (this.aiBotIntroMessageSubscription) {
      this.aiBotIntroMessageSubscription.unsubscribe();
      this.aiBotIntroMessageSubscription = undefined;
    }
    if (this.closeMiniChatRequestSubscription) {
      this.closeMiniChatRequestSubscription.unsubscribe();
      this.closeMiniChatRequestSubscription = undefined;
    }
    if (this.vivaBotHiddenSubscription) {
      this.vivaBotHiddenSubscription.unsubscribe();
      this.vivaBotHiddenSubscription = undefined;
    }
    if (this.openMiniChatRequestSubscription) {
      this.openMiniChatRequestSubscription.unsubscribe();
      this.openMiniChatRequestSubscription = undefined;
    }
  }

  private async processMsauthCallbackUrl(url: string): Promise<void> {
    try {
      const queryParams: Record<string, string> = {};
      const queryIndex = url.indexOf('?');

      if (queryIndex !== -1) {
        const queryString = url.substring(queryIndex + 1);
        const params = new URLSearchParams(queryString);

        const code = params.get('code');
        const error = params.get('error');
        const state = params.get('state');
        const errorDescription = params.get('error_description');

        if (code) queryParams['code'] = decodeURIComponent(code);
        if (error) queryParams['error'] = decodeURIComponent(error);
        if (errorDescription) queryParams['error_description'] = decodeURIComponent(errorDescription);
        if (state) queryParams['state'] = decodeURIComponent(state);
      } else {
        try {
          const parsed = new URL(url);
          const code = parsed.searchParams.get('code');
          const error = parsed.searchParams.get('error');
          const state = parsed.searchParams.get('state');
          const errorDescription = parsed.searchParams.get('error_description');

          if (code) queryParams['code'] = code;
          if (error) queryParams['error'] = error;
          if (errorDescription) queryParams['error_description'] = errorDescription;
          if (state) queryParams['state'] = state;
        } catch {
          const codeMatch = url.match(/[?&]code=([^&]+)/);
          const errorMatch = url.match(/[?&]error=([^&]+)/);
          const stateMatch = url.match(/[?&]state=([^&]+)/);
          const descMatch = url.match(/[?&]error_description=([^&]+)/);

          if (codeMatch) queryParams['code'] = decodeURIComponent(codeMatch[1]);
          if (errorMatch) queryParams['error'] = decodeURIComponent(errorMatch[1]);
          if (stateMatch) queryParams['state'] = decodeURIComponent(stateMatch[1]);
          if (descMatch) queryParams['error_description'] = decodeURIComponent(descMatch[1]);
        }
      }

      await this.router.navigate(['/login-callback'], { queryParams });
    } catch (e) {
      console.error('Error parsing msauth callback URL:', e, url);
      await this.router.navigate(['/login-callback']);
    }
  }

  /** Handles universal links, custom scheme URLs, and cold-start launch URLs. */
  private async processDeepLinkUrl(incomingUrl: string | null | undefined): Promise<void> {
    if (!incomingUrl || incomingUrl.startsWith('msauth')) {
      return;
    }

    const now = Date.now();
    if (this.lastDeepLinkUrl === incomingUrl && now - this.lastDeepLinkAt < 2000) {
      return;
    }
    this.lastDeepLinkUrl = incomingUrl;
    this.lastDeepLinkAt = now;

    try {
      let path: string;
      let queryParams = '';

      if (incomingUrl.includes('://') && !incomingUrl.startsWith('http://') && !incomingUrl.startsWith('https://')) {
        const schemeIndex = incomingUrl.indexOf('://');
        if (schemeIndex === -1) {
          return;
        }
        const afterScheme = incomingUrl.substring(schemeIndex + 3);
        const queryIndex = afterScheme.indexOf('?');
        if (queryIndex !== -1) {
          path = afterScheme.substring(0, queryIndex);
          queryParams = afterScheme.substring(queryIndex + 1);
        } else {
          path = afterScheme;
        }
      } else if (
        incomingUrl.startsWith('http://') ||
        incomingUrl.startsWith('https://') ||
        incomingUrl.includes(environment.webBase)
      ) {
        const url = incomingUrl.startsWith('http://') || incomingUrl.startsWith('https://')
          ? new URL(incomingUrl)
          : new URL(incomingUrl, window.location.origin);
        path = url.pathname;
        queryParams = url.searchParams.toString();
      } else {
        const slug = incomingUrl.split('.app').pop();
        if (!slug) {
          return;
        }
        let iosPath = slug.toLowerCase();
        if (iosPath.includes(environment.webBase)) {
          iosPath = iosPath.replace(environment.webBase, '');
          if (iosPath.startsWith('/')) {
            iosPath = iosPath.replace(/^\//, '');
          }
        }
        await this.applyDeepLinkNavigation(iosPath.startsWith('/') ? iosPath : `/${iosPath}`);
        return;
      }

      if (path.startsWith('/')) {
        path = path.substring(1);
      }
      if (path.endsWith('/')) {
        path = path.slice(0, -1);
      }

      if (path.includes('wearable-connected')) {
        await this.applyDeepLinkNavigation('/wearable-connected');
        return;
      }

      const navigationPath = queryParams ? `/${path}?${queryParams}` : `/${path}`;
      await this.applyDeepLinkNavigation(navigationPath);
    } catch (e) {
      console.error('Error processing deep link:', e, incomingUrl);
    }
  }

  private async applyDeepLinkNavigation(navigationPath: string): Promise<void> {
    await this.navigateToDeepLink(navigationPath);
    if (!this.isReady) {
      this.pendingDeepLinkNavigation = navigationPath;
      return;
    }
    await this.router.navigateByUrl(navigationPath, { replaceUrl: true });
    this.deepLinkNavigationApplied = true;
  }

  /** Applies a cold-start deeplink before the router outlet mounts. */
  private async flushPendingDeepLinkNavigation(): Promise<boolean> {
    if (this.deepLinkNavigationApplied) {
      return true;
    }

    let path = this.pendingDeepLinkNavigation;
    if (!path) {
      const pathJson = await SecureStorageService.get(SecureStorageKey.DeeplinkPath);
      if (pathJson) {
        try {
          const parsed = JSON.parse(pathJson);
          path = parsed?.path;
        } catch {
          // ignore malformed storage
        }
      }
    }

    if (!path) {
      return false;
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    this.pendingDeepLinkNavigation = null;
    await this.router.navigateByUrl(normalizedPath, { replaceUrl: true });
    this.deepLinkNavigationApplied = true;
    return true;
  }

  async navigateToDeepLink(path: string): Promise<void> {
    const linkPath = { path };
    const pathJSON = JSON.stringify(linkPath);
    await SecureStorageService.set(SecureStorageKey.DeeplinkPath, pathJSON);
    EventService.appDeeplinkEvent.emit(path);
  }

  // === CHAT ENTRYPOINT: tap on Viva icon ===
  public async aiChatClicked(): Promise<void> {
    if (this.inAIRedirect) return;

    this.inAIRedirect = true;
    try {
      const currentUrl = this.router.url;

      // Let active page craft the initial question
      await EventService.onAIChatStart.emit(currentUrl);

      // If page prepared an initial request, show mini bubble, else fallback to full page
      const req = MediaChatbotPage.initialRequest;
      if (req && req.sourceRoute && currentUrl.toLowerCase().startsWith(req.sourceRoute.toLowerCase())) {
        await this.openMiniChat(req);
      } else {
        const encodedUrl = encodeURIComponent(currentUrl);
        await this.navCtrl.navigateForward('home/mediaChatbot?src=' + encodedUrl);
      }
    } finally {
      this.inAIRedirect = false;
    }
  }

  private async openMiniChat(req: IInitialQuestion) {
    this.miniChatText = '';
    
    // Combine parts for mini chat (use simpler format for quick response)
    const miniSuffix = this._translator.translateText('app-component.ai-prompt.mini-response-suffix');
    const miniPrompt = `${req.dataContext}\n\n${req.promptInstructions}\n\n${miniSuffix}`;
    
    // Store the full request for later use in full chat
    // Don't clear MediaChatbotPage.initialRequest - keep it for the full chat
    this.miniChatQuestionText = req.dataContext;
    this.miniChatSourceRoute = req.sourceRoute;

    try {
      const response = await this._media.aiMiniQuery(miniPrompt, false);
      this.miniChatText = (response?.responseText ?? this._translate.instant('media-chat-bot-page.bot-no-answer'))
        .replace(/```[\w]*\n?/g, "")  // Remove code blocks
        .replace(/<body>/g, "").replace(/<\/body>/g, "")
        .replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>")  // Convert markdown to HTML
        .trim();
    } catch {
      this.miniChatText = this._translate.instant('media-chat-bot-page.bot-error');
    } finally {
      this.syncShowMoreInfoButtonForRoute();
    }
  }

  public async closeMiniChat() {
    this.miniChatText = "";
    this.syncShowMoreInfoButtonForRoute();
    // Reset AI state when closing mini chat without going to full chat
    MediaChatbotPage.initialRequest = undefined;
  }

  public async gotoFullChat() {
    // MediaChatbotPage.initialRequest should already be set from the initial tap
    // If not (edge case), reconstruct from stored values
    if (!MediaChatbotPage.initialRequest && this.miniChatQuestionText && this.miniChatSourceRoute) {
      MediaChatbotPage.initialRequest = {
        dataContext: this.miniChatQuestionText,
        promptInstructions: this._translator.translateText('app-component.ai-prompt.full-chat-fallback-instructions'),
        formatInstructions: '',
        sourceRoute: this.miniChatSourceRoute
      };
    }

    // Close mini chat but don't clear the initialRequest (full chat needs it)
    this.miniChatText = "";
    const encodedUrl = encodeURIComponent(this.router.url);
    await this.navCtrl.navigateForward('home/mediaChatbot?src=' + encodedUrl);
  }

  private isQuestionnaireRouteWithoutMoreInfo(url?: string): boolean {
    const u = (url ?? this.router.url ?? '').toLowerCase();
    return u.startsWith('/home/questionnaire/form') || u.startsWith('/home/questionnaire/result');
  }

  private syncShowMoreInfoButtonForRoute(url?: string, allowOnCurrentRoute = true): void {
    if (this.isQuestionnaireRouteWithoutMoreInfo(url)) {
      this.showMoreInfoButton = false;
      return;
    }

    this.showMoreInfoButton = allowOnCurrentRoute;
  }

  private isChatRouteAllowed(url: string): boolean {
    const allowedRoutes = [
      '/home/biometric-detail',
      '/home/group-score',
      '/home/healthscore', // normalize case
      ...flavourChatRoutes,
      '/home/wearables',
      '/home/points-detail',
      '/home/notification',
      '/home/questionnaire/form',
      '/home/questionnaire/result'];
    const u = (url || '').toLowerCase();
    return allowedRoutes.some(route => u.startsWith(route));
  }

  private isFullScreenRoute(url: string): boolean {
    const allowedRoutes = [
      '/v2/public/startup-hca',
      '/client/hca',
      '/v2/help-center',
      ...flavourFullScreenRoutes,
      '/v2/welcome/intro-page'
    ];
    const u = (url || '').toLowerCase();
    return allowedRoutes.some(route => u.startsWith(route));
  }

  async updateApp(): Promise<void> {
    await this._EnvironmentConfigService.OpenAppStore();
  }

  skipToMainContent(event: Event): void {
    event.preventDefault();
    const main = document.getElementById('main-content');
    if (!main) {
      return;
    }
    main.setAttribute('tabindex', '-1');
    main.focus();
  }
}
